import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, ClipboardCheck, ChevronDown, ShieldCheck, FileText, HandMetal } from 'lucide-react';
import { WAIVER_LABELS } from '../data/liabilityWaiver';
import { MEDICAL_LABELS } from '../data/medicalForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const val = (v) => (Array.isArray(v) ? v.join(', ') : v);
const answered = (v) => (Array.isArray(v) ? v.length > 0 : v !== undefined && v !== null && v !== '');

// Practitioner-only intake review: shows completion status, "Yes" health flags,
// and full answers for a client's Liability Waiver + Medical Disclosure.
const IntakeReview = ({ clientId, clientName }) => {
  const [waiver, setWaiver] = useState(null);
  const [medical, setMedical] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [expand, setExpand] = useState(null); // 'waiver' | 'medical'

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const [w, m] = await Promise.all([
        fetch(`${API}/liability-waiver/${clientId}`).then((r) => r.json()).catch(() => ({ exists: false })),
        fetch(`${API}/medical-disclosure/${clientId}`).then((r) => r.json()).catch(() => ({ exists: false })),
      ]);
      setWaiver(w.exists ? w : null);
      setMedical(m.exists ? m : null);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  // ── Build health flags (affirmative / safety-relevant answers) ──
  const flags = [];
  if (waiver?.answers) {
    Object.entries(waiver.answers).forEach(([k, v]) => {
      if (v === 'Yes' && WAIVER_LABELS[k]) flags.push({ src: 'Waiver', label: WAIVER_LABELS[k] });
    });
    ['explain_yes', 'explain_injuries', 'explain_surgeries', 'explain_medications'].forEach((k) => {
      if (answered(waiver.answers[k])) flags.push({ src: 'Waiver', label: WAIVER_LABELS[k], note: waiver.answers[k] });
    });
  }
  if (medical?.answers) {
    Object.entries(medical.answers).forEach(([k, v]) => {
      if (v === 'Yes' && MEDICAL_LABELS[k]) flags.push({ src: 'Medical', label: MEDICAL_LABELS[k] });
    });
  }

  const touch = waiver?.touch_consent;
  const noTouch = medical?.answers?.no_touch === 'Yes' ? medical.answers.no_touch_detail : null;

  const StatusPill = ({ ok, icon: Icon, label, date }) => (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-body text-xs border ${ok ? 'bg-forest/10 text-forest border-forest/20' : 'bg-stone-100 text-stone-400 border-stone-200'}`}>
      <Icon size={13} /> {label}: {ok ? (date ? `signed ${date}` : 'complete') : 'not yet'}
    </span>
  );

  const AnswerList = ({ labels, answers, order }) => {
    const keys = (order || Object.keys(answers)).filter((k) => answered(answers[k]) && labels[k]);
    if (!keys.length) return <p className="font-body text-xs text-stone-400 italic">No answers recorded.</p>;
    return (
      <dl className="space-y-1.5">
        {keys.map((k) => (
          <div key={k} className="text-xs font-body">
            <dt className="text-stone-500 leading-snug">{labels[k]}</dt>
            <dd className="text-stone-800 font-medium">{val(answers[k])}</dd>
          </div>
        ))}
      </dl>
    );
  };

  return (
    <div className="mb-6 bg-white border border-[#e2d4bf] rounded-xl overflow-hidden" data-testid="intake-review">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-stone-50 transition-colors"
        data-testid="intake-review-toggle"
      >
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-forest/10 text-forest flex-shrink-0">
          <ClipboardCheck size={16} />
        </span>
        <span className="flex-1">
          <span className="block font-heading text-sm text-forest">Intake review{clientName ? ` — ${clientName}` : ''}</span>
          <span className="block font-body text-xs text-stone-500">
            {loading ? 'Loading…' : `Waiver ${waiver ? '✓' : '—'} · Disclosure ${medical ? '✓' : '—'}${flags.length ? ` · ${flags.length} health flag${flags.length === 1 ? '' : 's'}` : ''}`}
          </span>
        </span>
        {flags.length > 0 && (
          <span className="inline-flex items-center gap-1 bg-red-50 text-red-600 border border-red-200 rounded-full px-2 py-0.5 text-[11px] font-body" data-testid="intake-flag-count">
            <AlertTriangle size={12} /> {flags.length}
          </span>
        )}
        <ChevronDown size={16} className={`text-stone-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-stone-100 pt-4" data-testid="intake-review-body">
          <div className="flex flex-wrap gap-2 mb-4">
            <StatusPill ok={!!waiver} icon={ShieldCheck} label="Waiver" date={waiver?.signed_date} />
            <StatusPill ok={!!medical} icon={FileText} label="Disclosure" date={medical?.signed_date} />
          </div>

          {/* Health flags */}
          {flags.length > 0 ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4" data-testid="intake-health-flags">
              <p className="flex items-center gap-1.5 font-heading text-sm text-red-700 mb-2">
                <AlertTriangle size={15} /> Health flags to review
              </p>
              <ul className="space-y-1.5">
                {flags.map((f, i) => (
                  <li key={i} className="font-body text-xs text-red-800 leading-snug">
                    <span className="uppercase tracking-wider text-[10px] text-red-500 mr-1.5">{f.src}</span>
                    {f.label}{f.note ? <span className="block text-red-700 italic mt-0.5">“{f.note}”</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            !loading && (waiver || medical) && (
              <p className="font-body text-xs text-stone-500 mb-4" data-testid="intake-no-flags">No affirmative health flags recorded.</p>
            )
          )}

          {/* Touch / hands-on preferences */}
          {(touch || noTouch) && (
            <div className="bg-[#f8f4ec] border border-stone-200 rounded-lg p-3 mb-4 text-xs font-body text-stone-700 flex items-start gap-2" data-testid="intake-touch">
              <HandMetal size={14} className="text-[#8a6a5a] flex-shrink-0 mt-0.5" />
              <span>
                {touch && <span className="block">Hands-on cueing consent: <span className="font-semibold">{touch}</span></span>}
                {noTouch && <span className="block">Do not touch / cue: <span className="font-semibold">{noTouch}</span></span>}
              </span>
            </div>
          )}

          {/* Full answers */}
          {waiver && (
            <div className="mb-2">
              <button onClick={() => setExpand((e) => (e === 'waiver' ? null : 'waiver'))} className="w-full flex items-center justify-between font-body text-xs text-forest py-2" data-testid="intake-expand-waiver">
                <span>View full waiver answers</span>
                <ChevronDown size={14} className={`transition-transform ${expand === 'waiver' ? 'rotate-180' : ''}`} />
              </button>
              {expand === 'waiver' && (
                <div className="pl-1 pb-2" data-testid="intake-waiver-answers">
                  <AnswerList labels={WAIVER_LABELS} answers={waiver.answers || {}} />
                  <p className="mt-2 text-xs font-body text-stone-500">Signed: <span className="text-stone-800 font-medium">{waiver.signed_name || '—'}</span> · {waiver.signed_date || '—'}</p>
                </div>
              )}
            </div>
          )}
          {medical && (
            <div>
              <button onClick={() => setExpand((e) => (e === 'medical' ? null : 'medical'))} className="w-full flex items-center justify-between font-body text-xs text-forest py-2" data-testid="intake-expand-medical">
                <span>View full disclosure answers</span>
                <ChevronDown size={14} className={`transition-transform ${expand === 'medical' ? 'rotate-180' : ''}`} />
              </button>
              {expand === 'medical' && (
                <div className="pl-1 pb-2" data-testid="intake-medical-answers">
                  <AnswerList labels={MEDICAL_LABELS} answers={medical.answers || {}} />
                  {Array.isArray(medical.body_map) && medical.body_map.length > 0 && (
                    <p className="mt-2 text-xs font-body text-stone-500">Body map: <span className="text-stone-800 font-medium">{medical.body_map.length} marker(s)</span></p>
                  )}
                  <p className="mt-1 text-xs font-body text-stone-500">Signed: <span className="text-stone-800 font-medium">{medical.signed_name || '—'}</span> · {medical.signed_date || '—'}</p>
                </div>
              )}
            </div>
          )}

          {!loading && !waiver && !medical && (
            <p className="font-body text-xs text-stone-400 italic" data-testid="intake-empty">This client hasn't completed their waiver or disclosure yet.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default IntakeReview;
